import {
  ShieldCheck,
  Banknote,
  Clock,
  CreditCard,
  ArrowRight,
  CheckCircle2,
} from "lucide-react";

const advantages = [
  {
    icon: ShieldCheck,
    label: "For Buyers",
    title: "Procure from verified sellers",
    desc: "Suppliers with proven trade history, AI-verified through real transaction data. No more gambling on unknown factories.",
    highlights: [
      "Proven track records",
      "AI-verified suppliers",
      "Zero risk sourcing",
    ],
  },
  {
    icon: Banknote,
    label: "For Sellers",
    title: "Get paid immediately",
    desc: "No waiting 15–60 days for cash. Get paid once goods are quality - checked — payment against Bill of Lading.",
    highlights: ["Instant payment", "Against QA & BL", "Cash flow unlocked"],
  },
  {
    icon: Clock,
    label: "Quality Assurance",
    title: "Quality-backed payment",
    desc: "Third-party QA vendors inspect on-site. Container loaded before payment released. Full trust, zero risk.",
    highlights: [
      "On-site inspection",
      "Pre-shipment QA",
      "Both sides protected",
    ],
  },
  {
    icon: CreditCard,
    label: "For Buyers",
    title: "Flexible payment terms",
    desc: "Pay net 15 to 60 days. We provide extended payment terms for you, subject to credit check & rating.",
    highlights: ["Net 15/30/60", "Payment terms built-in", "Credit-backed"],
  },
];

export default function AdvantagesSection() {
  return (
    <section className="py-10 md:py-16 relative overflow-hidden">
      <div className="container px-4 relative">
        <div className="max-w-3xl mx-auto text-center mb-10">
          <p className="text-sm font-semibold text-primary mb-3 tracking-wide uppercase">
            Advantages
          </p>
          <h2 className="text-3xl md:text-[2.75rem] font-display font-bold text-foreground leading-tight tracking-tight mb-2">
            Our AI reasons across trade documents,
            <br className="hidden md:block" />
            <span className="text-gradient">
              price curves & supply signals.
            </span>
          </h2>
        </div>

        {/* 2x2 grid — each card has content + highlights inline */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-5xl mx-auto">
          {advantages.map((a) => (
            <div
              key={a.title}
              className="bg-card border border-border rounded-2xl p-6 hover:border-primary/30 hover:shadow-lg hover:shadow-primary/5 transition-all duration-300 group relative overflow-hidden"
            >
              <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-primary/40 to-transparent rounded-l-2xl opacity-0 group-hover:opacity-100 transition-opacity" />

              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                  <a.icon className="w-4 h-4 text-primary" />
                </div>
                <span className="text-[10px] font-bold text-primary uppercase tracking-widest bg-primary/10 px-2.5 py-0.5 rounded-full">
                  {a.label}
                </span>
              </div>

              <h3 className="text-base font-display font-bold text-foreground mb-1.5 tracking-tight">
                {a.title}
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                {a.desc}
              </p>

              <div className="flex flex-wrap gap-2">
                {a.highlights.map((h, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center gap-1.5 text-[11px] font-medium text-primary bg-primary/[0.06] border border-primary/10 rounded-full px-2.5 py-1"
                  >
                    <CheckCircle2 className="w-3 h-3" />
                    {h}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* CTA banner */}
        <div className="mt-10 bg-gradient-to-r from-primary to-primary/80 rounded-2xl p-8 md:p-10 text-center relative overflow-hidden max-w-5xl mx-auto">
          <div
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage:
                "radial-gradient(circle at 20% 50%, hsl(var(--primary-foreground)) 1px, transparent 1px)",
              backgroundSize: "20px 20px",
            }}
          />
          <div className="relative">
            <p className="text-xl md:text-2xl font-display font-bold text-primary-foreground tracking-tight leading-snug">
              Weeks of emails and RFQs → now a single request.
            </p>
            <p className="text-sm text-primary-foreground/70 mt-2 max-w-lg mx-auto">
              All-in pricing. Full execution. We're not a broker — we're your
              procurement partner.
            </p>
            <a
              href="#contact"
              className="inline-flex items-center justify-center gap-2 mt-6 px-7 py-3 text-sm font-bold rounded-lg bg-primary-foreground text-primary hover:bg-primary-foreground/90 transition-colors"
            >
              Get a Quote <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
